import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Font, PDFDownloadLink } from '@react-pdf/renderer';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { analyticsLogger, PageType, ReportType } from 'commons/src/analytics';
import { INSIGHT_DOWNLOADED_REPORT } from 'commons/src/analytics/AnalyticsEvents';
import { mapDeviceType } from 'commons/src/commonFunctions';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { dateFormats } from 'commons/src/constants';
import DemiBold from 'commons/src/fonts/open-sans-v28-latin-600.ttf';
import RegularFont from 'commons/src/fonts/open-sans-v28-latin-regular.ttf';
import { AnyDeviceType, BuildingType, DeviceWithKeyInfo, SegmentProperties } from 'commons/src/models/commonTypeScript';
import { IaqInsightData, PDFContext, ReportSensorThresholds } from '../../../../models/common';
import { generateImage } from '../../GraphConfig';
import styles from '../iaqInsight2.module.scss';
import IaqInsight2PdfDocument from './IaqInsight2PdfDocument';
import { sanitizeFileName, SpaceMeasurementDataColumns } from './utils/pdfHelpers';

Font.register({ family: 'DemiBold', src: DemiBold });
Font.register({ family: 'Regular', src: RegularFont });

type IaqInsight2PdfGeneratorProps = {
    dateFormat: keyof typeof dateFormats;
    toDate: string | null;
    fromDate: string | null;
    building: BuildingType | undefined;
    reports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
    devices: { [serialNumber: string]: DeviceWithKeyInfo };
    segmentProperties: { [serialNumber: string]: SegmentProperties };
    logo?: string;
    comment: string;
    reportDetails: {
        deviceTypes: string[];
        userName: string;
        reportId: string;
    };
    hideCharts?: boolean;
};

type IaqInsight2PdfDocumentProperties = {
    building: BuildingType;
    translationFunction: PDFContext;
    dateFormat: keyof typeof dateFormats;
    reports: { [sensor: string]: IaqInsightData };
    thresholds: { [sensor: string]: ReportSensorThresholds };
    comment: string;
    deviceTypesInReport: string[];
    spaceMeasurementColumnsBySensorType: { [sensor: string]: SpaceMeasurementDataColumns };
    listOfImages: { data: string; serialNumber: string }[];
    reportDetails: {
        deviceTypes: string[];
        userName: string;
        reportId: string;
    };
    fromDate: string | null;
    toDate: string | null;
    hideCharts?: boolean;
};

type ChartImage = { data: string; serialNumber: string };

const CHART_READINESS_CHECK_INTERVAL_MS = 100;
const CHART_GENERATION_TIMEOUT_MS = 5000;
const MAX_CHART_READINESS_ATTEMPTS = Math.ceil(CHART_GENERATION_TIMEOUT_MS / CHART_READINESS_CHECK_INTERVAL_MS);
const BROWSER_YIELD_DELAY_MS = 100;
const FINAL_RENDER_DELAY_MS = 500;
const AUTO_DOWNLOAD_DELAY_MS = 500;
const OBJECT_URL_CLEANUP_DELAY_MS = 100;

const logDownloadReport = (): void => {
    analyticsLogger(INSIGHT_DOWNLOADED_REPORT, { pageType: PageType.Insight, reportType: ReportType.IAQInsight });
};

const waitForDOMReady = (): Promise<void> => {
    return new Promise(resolve => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                resolve(undefined);
            });
        });
    });
};

const waitForDelay = (ms: number): Promise<void> => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve();
        }, ms);
    });
};

const yieldToBrowser = (): Promise<void> => {
    return new Promise(resolve => {
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(() => resolve(undefined), { timeout: BROWSER_YIELD_DELAY_MS });
        } else {
            setTimeout(() => resolve(undefined), BROWSER_YIELD_DELAY_MS);
        }
    });
};

const getChartIdFromChart = (chart: Highcharts.Chart | null | undefined): string | null => {
    if (!chart?.userOptions?.chart) return null;
    const chartOptions = chart.userOptions.chart as { id?: string };
    return chartOptions?.id || null;
};

const findChartsBySensorIds = (sensorIds: string[]): Highcharts.Chart[] => {
    const allCharts = [...Highcharts.charts].filter((chart): chart is Highcharts.Chart => chart !== undefined);
    return allCharts.filter(chart => {
        const chartId = getChartIdFromChart(chart);
        return chartId && sensorIds.includes(chartId);
    });
};

const areAllChartsRendered = (sensorIds: string[]): boolean => {
    const foundCharts = findChartsBySensorIds(sensorIds);

    if (foundCharts.length < sensorIds.length) {
        return false;
    }

    return sensorIds.every(sensorId => {
        const chart = foundCharts.find(c => getChartIdFromChart(c) === sensorId);
        const renderer = chart?.renderer as { isHidden?: () => boolean } | undefined;
        return chart && renderer && !renderer.isHidden?.();
    });
};

const waitForChartsToBeReady = async (sensorIds: string[]): Promise<boolean> => {
    const checkCharts = async (attempt: number): Promise<boolean> => {
        if (attempt >= MAX_CHART_READINESS_ATTEMPTS) {
            return false;
        }

        if (areAllChartsRendered(sensorIds)) {
            return true;
        }

        await waitForDelay(CHART_READINESS_CHECK_INTERVAL_MS);
        return checkCharts(attempt + 1);
    };

    return checkCharts(0);
};

const generateChartImageForSensor = (
    sensor: string,
    onComplete: (image: ChartImage) => void,
    onError: (error: boolean) => void
): Promise<void> => {
    return new Promise<void>(resolve => {
        const timeout = setTimeout(() => {
            resolve();
        }, CHART_GENERATION_TIMEOUT_MS);

        const handleImageGenerated = (image: ChartImage): void => {
            clearTimeout(timeout);
            onComplete(image);
            resolve();
        };

        try {
            generateImage(handleImageGenerated, onError, sensor);
        } catch {
            clearTimeout(timeout);
            resolve();
        }
    });
};

const generateFileName = (
    building: BuildingType | undefined,
    toDate: string | null,
    dateFormat: keyof typeof dateFormats
): string => {
    const sanitizedBuildingName = building ? sanitizeFileName(building.name || '') : '';
    const dateForFilename = toDate
        ? moment(toDate, dateFormats[dateFormat].format).format('DDMMYYYY')
        : moment().format('DDMMYYYY');
    return `${dateForFilename}_IAQ_Report_${sanitizedBuildingName}.pdf`;
};

const triggerPdfDownload = (blob: Blob, fileName: string): void => {
    logDownloadReport();

    const objectUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = objectUrl;
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
    }, OBJECT_URL_CLEANUP_DELAY_MS);
};

export const IaqInsight2PdfGenerator = (props: IaqInsight2PdfGeneratorProps): React.ReactElement | null => {
    const {
        dateFormat,
        toDate,
        fromDate,
        building,
        reports,
        thresholds,
        devices,
        segmentProperties,
        comment,
        reportDetails,
        hideCharts,
    } = props;
    const { t: translationFunction } = useTranslation();

    const [chartImages, setChartImages] = useState<ChartImage[]>([]);
    const [isGeneratingCharts, setIsGeneratingCharts] = useState(false);
    const [chartsReady, setChartsReady] = useState(false);

    const pdfBlobRef = useRef<Blob | null>(null);
    const pdfUrlRef = useRef<string | null>(null);
    const downloadTriggeredRef = useRef<boolean>(false);

    const fileName = useMemo(() => generateFileName(building, toDate, dateFormat), [building, toDate, dateFormat]);

    const sensorsWithChartData = useMemo(() => {
        return Object.keys(reports).filter(sensor => {
            const report = reports[sensor];
            return report.averageGraph ? report.averageGraph.length > 0 : false;
        });
    }, [reports]);

    const formattedDeviceTypes = useMemo(
        () =>
            reportDetails.deviceTypes.map((deviceType, index) => {
                const mappedDeviceTypeName = mapDeviceType(deviceType as AnyDeviceType);
                const translatedDeviceTypeFullName = translationFunction(`${mappedDeviceTypeName}FullName`);
                const separator =
                    reportDetails.deviceTypes.length > 1 && index < reportDetails.deviceTypes.length - 1 ? ', ' : '';
                return `Airthings ${translatedDeviceTypeFullName}${separator}`;
            }),
        [reportDetails.deviceTypes, translationFunction]
    );

    const spaceMeasurementColumnsBySensorType = useMemo(
        () =>
            Object.keys(reports).reduce((accumulator, sensorTypeKey) => {
                const spaceMeasurementColumns = Object.keys(reports[sensorTypeKey].devicesOpeningHoursValues || {})
                    .map(deviceSerialNumber => {
                        const spaceData = reports[sensorTypeKey].devicesOpeningHoursValues[deviceSerialNumber] as
                            | {
                                  name?: string;
                                  average: number;
                                  highestMeasurement: number;
                                  lowestMeasurement: number;
                              }
                            | undefined;
                        const deviceInfo = devices[deviceSerialNumber];
                        const segmentProps = segmentProperties[deviceSerialNumber];

                        const spaceName = spaceData?.name || deviceInfo?.segmentName || deviceSerialNumber;

                        return {
                            ...spaceData,
                            name: spaceName,
                            serialNumber: deviceSerialNumber,
                            roomType: segmentProps?.roomType,
                            floor: segmentProps?.floor,
                        };
                    })
                    .sort((a, b) => a.name.localeCompare(b.name));

                return {
                    ...accumulator,
                    [sensorTypeKey]: spaceMeasurementColumns,
                };
            }, {}),
        [reports, devices, segmentProperties]
    );

    const pdfDocumentProperties = useMemo((): IaqInsight2PdfDocumentProperties | undefined => {
        if (building && chartsReady) {
            const formattedFromDate = fromDate
                ? moment(fromDate, dateFormats[dateFormat].format).format(dateFormats[dateFormat].shortFormat)
                : null;
            const formattedToDate = toDate
                ? moment(toDate, dateFormats[dateFormat].format).format(dateFormats[dateFormat].shortFormat)
                : null;

            return {
                building,
                translationFunction,
                dateFormat,
                thresholds,
                reports,
                comment,
                spaceMeasurementColumnsBySensorType,
                listOfImages: chartImages,
                reportDetails,
                deviceTypesInReport: formattedDeviceTypes,
                fromDate: formattedFromDate,
                toDate: formattedToDate,
                hideCharts: hideCharts || false,
            };
        }
        return undefined;
    }, [
        building,
        chartsReady,
        translationFunction,
        dateFormat,
        thresholds,
        reports,
        comment,
        spaceMeasurementColumnsBySensorType,
        chartImages,
        reportDetails,
        formattedDeviceTypes,
        fromDate,
        toDate,
    ]);

    const resetDownloadState = useCallback(() => {
        downloadTriggeredRef.current = false;
        pdfBlobRef.current = null;
        pdfUrlRef.current = null;
    }, []);

    const generateAllChartImages = useCallback(async (): Promise<void> => {
        resetDownloadState();

        if (hideCharts || sensorsWithChartData.length === 0) {
            setChartsReady(true);
            return;
        }

        setIsGeneratingCharts(true);

        try {
            await waitForDOMReady();

            await waitForChartsToBeReady(sensorsWithChartData);

            if (Highcharts.charts.length === 0) {
                return;
            }

            await waitForDelay(FINAL_RENDER_DELAY_MS);

            const generatedImages: ChartImage[] = [];

            await sensorsWithChartData.reduce(async (previousPromise, sensor, index) => {
                await previousPromise;

                await generateChartImageForSensor(
                    sensor,
                    image => {
                        generatedImages.push(image);
                        setChartImages([...generatedImages]);
                    },
                    () => {
                        /* onError: continue without this chart */
                    }
                );

                if (index < sensorsWithChartData.length - 1) {
                    await yieldToBrowser();
                }
            }, Promise.resolve());

            setChartsReady(true);
        } catch {
            /* Chart generation failed; continue without charts */
        } finally {
            setIsGeneratingCharts(false);
        }
    }, [sensorsWithChartData, resetDownloadState, hideCharts]);

    const handleAutoDownload = useCallback(
        (blob: Blob) => {
            if (downloadTriggeredRef.current) {
                return;
            }

            downloadTriggeredRef.current = true;
            setTimeout(() => {
                triggerPdfDownload(blob, fileName);
            }, AUTO_DOWNLOAD_DELAY_MS);
        },
        [fileName]
    );

    const renderInitialDownloadButton = (): React.ReactElement => {
        return (
            <div className={styles.downloadButtonWrapper}>
                <button
                    type="button"
                    className={styles.downloadButton}
                    onClick={(): void => {
                        if (!isGeneratingCharts) {
                            logDownloadReport();
                            generateAllChartImages();
                        }
                    }}
                    disabled={isGeneratingCharts}
                >
                    {isGeneratingCharts ? <span className={styles.buttonSpinner} /> : <MaterialIcon name="download" />}
                    <span>{translationFunction('Download as pdf')}</span>
                </button>
            </div>
        );
    };

    const renderLoadingState = (): React.ReactElement => {
        return (
            <div className={styles.downloadButtonWrapper}>
                <div className={styles.downloadButton}>
                    <span className={styles.buttonSpinner} />
                    <span>{translationFunction('Download as pdf')}</span>
                </div>
            </div>
        );
    };

    const renderPdfDownloadLink = (properties: IaqInsight2PdfDocumentProperties): React.ReactElement => {
        return (
            <div className={styles.downloadButtonWrapper}>
                <PDFDownloadLink document={<IaqInsight2PdfDocument {...properties} />} fileName={fileName}>
                    {({ loading, blob, url, error }): React.ReactElement => {
                        if (error) {
                            downloadTriggeredRef.current = false;
                            return (
                                <div style={{ padding: '10px 16px', color: 'red' }}>
                                    Error generating PDF. Please try again.
                                </div>
                            );
                        }

                        if (blob && !pdfBlobRef.current) {
                            pdfBlobRef.current = blob;
                        }
                        if (url && !pdfUrlRef.current) {
                            pdfUrlRef.current = url;
                        }

                        if (loading) {
                            return (
                                <div className={styles.downloadButton}>
                                    <span className={styles.buttonSpinner} />
                                    <span>{translationFunction('Download as pdf')}</span>
                                </div>
                            );
                        }

                        if (blob && !downloadTriggeredRef.current) {
                            handleAutoDownload(blob);
                        }

                        return (
                            <div className={styles.downloadButton}>
                                <MaterialIcon name="download" />
                                <span>{translationFunction('Download as pdf')}</span>
                            </div>
                        );
                    }}
                </PDFDownloadLink>
            </div>
        );
    };

    if (!building) {
        return <div style={{ display: 'none' }} />;
    }

    if (!chartsReady) {
        return renderInitialDownloadButton();
    }

    if (!pdfDocumentProperties) {
        return renderLoadingState();
    }

    return renderPdfDownloadLink(pdfDocumentProperties);
};

export default IaqInsight2PdfGenerator;
